import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import SettingToggle from '../shared/SettingToggle';
import SegmentedTabs from '../shared/SegmentedTabs';
import {
    IconAdjustmentsHorizontal,
    IconBrowser,
    IconDeviceDesktop,
    IconDeviceLaptop,
    IconDeviceMobile,
    IconDeviceTablet,
    IconDeviceTv,
    IconDownload,
    IconFileMinus,
    IconFileTypeBmp,
    IconFileTypeJpg,
    IconFileTypePng,
    IconFileZip,
    IconPhoto,
    IconPercentage,
    IconPhotoScan,
    IconRefresh,
    IconWand,
    IconWeight,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { zipImageFiles } from '../../pdf/pdf-to-image/pdfToImageEngine';
import useImageFileEdits from '../../components/image/useImageFileEdits';
import useImagePaste from '../../components/image/useImagePaste';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ImageGridPreview from '../shared/ImageGridPreview';
import ToolCard from '../../components/common/ToolCard';
import ImagePreviewModal from '../../../../../components/common/ImagePreviewModal';
import {
    IMAGE_INPUT_ACCEPT as ACCEPT,
    compressImageFile,
    compressedImageName,
    isSupportedImageFile,
    canEncodeFormat,
    canPreserveExif,
    formatMimeType,
    DEFAULT_COMPRESS_SETTINGS,
    MAX_DIMENSION_OPTIONS,
} from './compressImageEngine';
import { useCompareUrls } from '../shared/heicDisplay';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const MAX_FILE_BYTES = 500 * 1024 * 1024;
// Sizes a preset target can realistically be met at — a bigger picture cannot get that small
// without wrecking the quality. Custom is not limited.
const PRESET_MAX_DIMENSIONS = {
    100: ['800'],
    300: ['1024', '800'],
};
const MIN_TARGET_KB = 5;
const MAX_TARGET_KB = 51200;



// One icon per max-size choice: a bigger screen for a bigger picture.
const DIMENSION_ICONS = {
    original: IconPhoto,
    3840: IconDeviceTv,
    2560: IconDeviceDesktop,
    1920: IconDeviceLaptop,
    1280: IconDeviceTablet,
    1024: IconBrowser,
    800: IconDeviceMobile,
};

const FORMAT_ICONS = {
    current: IconWand,
    webp: IconPhoto,
    jpg: IconFileTypeJpg,
    png: IconFileTypePng,
    avif: IconPhotoScan,
    bmp: IconFileTypeBmp,
};

function segBtnClass(active) {
    return `flex flex-1 min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

const fileKey = (file) => `${file.name}-${file.size}`;

export default function CompressImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [quality, setQuality] = useState(DEFAULT_COMPRESS_SETTINGS.quality);
    const [maxDimension, setMaxDimension] = useState(DEFAULT_COMPRESS_SETTINGS.maxDimension);
    const [preserveExif, setPreserveExif] = useState(DEFAULT_COMPRESS_SETTINGS.preserveExif);
    const [keepOriginalName, setKeepOriginalName] = useState(DEFAULT_COMPRESS_SETTINGS.keepOriginalName);
    const [format, setFormat] = useState('original');
    // Two ways to say how small: a quality percentage, or a file size to fit under.
    const [mode, setMode] = useState('percent'); // 'percent' | 'size'
    const [sizePreset, setSizePreset] = useState('100'); // '100' | '300' | 'custom' (KB)
    const [customKb, setCustomKb] = useState('200');
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);


    // A preset size is only worth promising in JPG, which shrinks smoothly; Custom leaves
    // the format free, since the person asking for it knows what they are after.
    const sizeMode = mode === 'size';
    const presetLocksJpg = sizeMode && sizePreset !== 'custom';
    const effectiveFormat = presetLocksJpg ? 'jpg' : format;
    const allowedDimensions = presetLocksJpg ? PRESET_MAX_DIMENSIONS[sizePreset] : null;
    // A preset can only use the sizes it allows; fall back to the largest of them.
    const effectiveMaxDimension = allowedDimensions && !allowedDimensions.includes(maxDimension)
        ? allowedDimensions[0]
        : maxDimension;
    const targetKb = sizeMode ? Number(sizePreset === 'custom' ? customKb : sizePreset) : 0;
    const targetValid = !sizeMode || (Number.isFinite(targetKb) && targetKb >= MIN_TARGET_KB && targetKb <= MAX_TARGET_KB);

    const invalidateResults = () => {
        setResults([]);
        setError(null);
        setProgress(null);
        setPreviewIndex(0);
    };

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const images = list.filter(isSupportedImageFile);
        if (!images.length) {
            const msg = t('compressImage.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        let tooLarge = false;
        setFiles((prev) => {
            const next = [...prev];
            for (const file of images) {
                if (file.size > MAX_FILE_BYTES) {
                    tooLarge = true;
                    continue;
                }
                if (next.some((f) => fileKey(f) === fileKey(file))) continue;
                next.push(file);
            }
            return next;
        });

        if (tooLarge) {
            const msg = t('compressImage.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } else {
            setError(null);
        }
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied picture straight in.
    useImagePaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        invalidateResults();
    };

    // Crop / rotate / duplicate / delete, shared with every other image tool.
    // The edits rewrite the picked file, so the pipeline below is untouched.
    const { actions: fileActions, cropDialog } = useImageFileEdits({
        files,
        onReplace: (index, file) => {
            setFiles((prev) => prev.map((item, i) => (i === index ? file : item)));
            invalidateResults();
        },
        onInsert: (index, file) => {
            setFiles((prev) => {
                const next = [...prev];
                next.splice(index + 1, 0, file);
                return next;
            });
            invalidateResults();
        },
        onRemove: removeFile,
    });

    const handleReset = () => {
        setFiles([]);
        invalidateResults();
    };

    // Files with no result yet — adding pictures to a finished batch queues only
    // the new ones, so nothing already compressed is redone.
    const pendingIndexes = useMemo(
        () => files.reduce((acc, _file, i) => (results[i] ? acc : [...acc, i]), []),
        [files, results],
    );
    const allDone = files.length > 0 && pendingIndexes.length === 0;
    const canRun = pendingIndexes.length > 0 && targetValid;

    const handleCompress = async () => {
        if (!canRun || processing) return;

        recordToolUsage('image/compress', { category: 'image' });

        setProcessing(true);
        setError(null);
        const queue = pendingIndexes;
        let missedTarget = false;

        try {
            for (let k = 0; k < queue.length; k += 1) {
                const i = queue[k];
                const file = files[i];
                setProgress({ fileIndex: k + 1, fileTotal: queue.length });

                const blob = await compressImageFile(file, sizeMode
                    ? { targetBytes: targetKb * 1024, maxDimension: effectiveMaxDimension, preserveExif, format: effectiveFormat }
                    : { quality, maxDimension, preserveExif, format });

                // Written at the file's own index (results line up with files) and
                // published immediately, so its card stops spinning right away.
                if (sizeMode && blob.size > targetKb * 1024 * 1.02) missedTarget = true;
                setResults((prev) => {
                    const next = [...prev];
                    next[i] = {
                        name: compressedImageName(file, blob, { keepOriginalName }),
                        originalSize: file.size,
                        compressedSize: blob.size,
                        blob,
                    };
                    return next;
                });
            }
            if (missedTarget) {
                notifications.show({ title: t('common.error'), message: t('compressImage.targetMissed'), color: 'yellow' });
            } else {
                notifications.show({ title: t('common.success'), message: t('compressImage.done'), color: 'green' });
            }
        } catch {
            const msg = t('compressImage.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const handleDownload = async () => {
        if (!results.length) return;

        if (results.length === 1) {
            downloadBlob(results[0].blob, results[0].name);
            return;
        }

        const zip = await zipImageFiles(results.map((r) => ({ name: r.name, blob: r.blob })));
        downloadBlob(zip, 'compressed-images.zip');
    };

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun,
        processing,
        hasResult: results.length > 0,
        hasError: !!error,
    });

    // ── Before/after lightbox ──
    // Object URLs live as long as the results do; the modal only ever gets URLs,
    // never blobs, so they are made once here instead of per render.
    const [lightboxIndex, setLightboxIndex] = useState(null);
    // HEIC originals are decoded for the "before" pane (see shared/heicDisplay).
    const compareUrls = useCompareUrls(results, files);

    const compareItems = useMemo(
        () => results
            .map((r, i) => (r ? {
                id: i,
                fileName: r.name,
                mimeType: r.blob.type || 'image/jpeg',
                aiImage: compareUrls[i]?.after,
                // What the shared modal calls the "before" pane.
                uploadedImage: compareUrls[i]?.before,
            } : null))
            .filter((item) => item?.aiImage),
        [results, compareUrls],
    );

    const openLightbox = (index) => {
        if (!results[index]) return;
        setPreviewIndex(index);
        setLightboxIndex(index);
    };

    const doneResults = useMemo(() => results.filter(Boolean), [results]);

    const successLabel = doneResults.length > 1
        ? `${t('compressImage.resultsTitle')} (${toBnDigits(doneResults.length)})`
        : t('compressImage.done');

    const processingLabel = progress?.fileTotal > 1
        ? t('compressImage.progressFileOnly', {
            file: toBnDigits(progress.fileIndex),
            files: toBnDigits(progress.fileTotal),
        })
        : t('compressImage.compressing');

    const overallProgress = progress?.fileTotal
        ? Math.round((progress.fileIndex / progress.fileTotal) * 100)
        : undefined;

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('compressImage.done'),
    });

    return (
        <>
        <ToolPageLayout
            slug="image/compress"
            subtitleKey="compressImage.subtitle"
            status={status}
            statusText={allDone && !processing ? successLabel : statusText}
            onReset={files.length || error ? handleReset : undefined}
            // Before a run the bar runs the tool; once every picture is done it downloads.
            primaryAction={allDone && !processing ? {
                label: doneResults.length > 1 ? t('compressImage.downloadZip') : t('compressImage.download'),
                onClick: handleDownload,
                icon: IconDownload,
            } : {
                label: t('compressImage.compress'),
                onClick: handleCompress,
                disabled: !canRun || processing,
                loading: processing,
            }}
            // Drops the results only — the pictures and settings stay loaded, so another
            // run is one click away. Clearing everything is what রিসেট is for.
            secondaryAction={allDone && !processing ? {
                label: t('compressImage.startOver'),
                onClick: invalidateResults,
                icon: IconRefresh,
            } : undefined}
            showTrust={false}
            settings={(
                <div className="flex flex-col gap-3">
                    {/* Files are picked in the preview panel, so the left column
                        carries settings only — this input is what both the empty
                        state and the grid's "+" tile open. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {/* One card, top to bottom in the order people decide: how much
                        quality to give up, how big the picture may stay, then the two
                        yes/no extras. Every change drops finished results, since they
                        no longer match the settings. */}
                    <ToolCard title={t('compressImage.modeTitle')}>
                        <div className="flex flex-col gap-4">
                            <SegmentedTabs
                                ariaLabel={t('compressImage.modeTitle')}
                                stacked
                                value={mode}
                                disabled={processing}
                                onChange={(next) => { setMode(next); invalidateResults(); }}
                                options={[
                                    { id: 'percent', label: t('compressImage.modeTabs.percent'), icon: IconPercentage, hint: t('compressImage.modeHints.percent') },
                                    { id: 'size', label: t('compressImage.modeTabs.size'), icon: IconWeight, hint: t('compressImage.modeHints.size') },
                                ]}
                            />

                            {sizeMode ? (
                                <div>
                                    <span className="mb-1.5 block text-[13.5px] font-semibold text-[#1C1815] dark:text-zinc-100">
                                        {t('compressImage.targetSizeLabel')}
                                    </span>
                                    <SegmentedTabs
                                        ariaLabel={t('compressImage.targetSizeLabel')}
                                        stacked
                                        value={sizePreset}
                                        disabled={processing}
                                        onChange={(next) => { setSizePreset(next); invalidateResults(); }}
                                        options={[
                                            { id: '100', label: `${toBnDigits(100)} KB`, icon: IconFileMinus, hint: t('compressImage.sizeHints.100') },
                                            { id: '300', label: `${toBnDigits(300)} KB`, icon: IconFileZip, hint: t('compressImage.sizeHints.300') },
                                            { id: 'custom', label: t('compressImage.sizeCustom'), icon: IconAdjustmentsHorizontal, hint: t('compressImage.sizeHints.custom') },
                                        ]}
                                    />
                                    {sizePreset === 'custom' ? (
                                        <div className="mt-2.5">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    inputMode="numeric"
                                                    min={MIN_TARGET_KB}
                                                    max={MAX_TARGET_KB}
                                                    value={customKb}
                                                    disabled={processing}
                                                    onChange={(e) => { setCustomKb(e.target.value); invalidateResults(); }}
                                                    aria-label={t('compressImage.targetSizeLabel')}
                                                    className="h-[42px] w-full rounded-xl border border-[#ECE8E4] bg-white px-3 text-[13.5px] tabular-nums text-[#1C1815] outline-none transition-colors focus:border-amber-400 disabled:opacity-45 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                                />
                                                <span className="text-[13px] font-semibold text-[#6B635C] dark:text-zinc-400">KB</span>
                                            </div>
                                            {!targetValid ? (
                                                <div className="mt-1 text-[11.5px] text-red-500">
                                                    {t('compressImage.targetInvalid', { min: toBnDigits(MIN_TARGET_KB), max: toBnDigits(MAX_TARGET_KB) })}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                    <div className="mt-1.5 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                        {t('compressImage.sizeHint')}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <label className="block">
                                        <div className="mb-1.5 flex items-center justify-between">
                                            <span className="text-[13.5px] font-semibold text-[#1C1815] dark:text-zinc-100">
                                                {t('compressImage.qualityLabel')}
                                            </span>
                                            <span className="text-[13px] font-semibold tabular-nums text-[#6B635C] dark:text-zinc-400">
                                                {toBnDigits(quality)}%
                                            </span>
                                        </div>
                                        <input
                                            type="range"
                                            min={10}
                                            max={100}
                                            step={1}
                                            value={quality}
                                            disabled={processing}
                                            onChange={(e) => { setQuality(Number(e.target.value)); invalidateResults(); }}
                                            className="w-full accent-amber-500 disabled:opacity-45"
                                        />
                                        <div className="mt-1 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                            {t('compressImage.qualityHint')}
                                        </div>
                                    </label>
                                </>
                            )}

                            <div>
                                <span className="mb-1.5 block text-[13.5px] font-semibold text-[#1C1815] dark:text-zinc-100">
                                    {t('compressImage.maxDimensionLabel')}
                                </span>
                                <SegmentedTabs
                                    ariaLabel={t('compressImage.maxDimensionLabel')}
                                    stacked
                                    value={effectiveMaxDimension}
                                    disabled={processing}
                                    columns={4}
                                    onChange={(next) => { setMaxDimension(next); invalidateResults(); }}
                                    options={MAX_DIMENSION_OPTIONS.map((opt) => ({
                                        id: opt.id,
                                        label: opt.value ? `${toBnDigits(opt.value)}px` : t('compressImage.maxDimensionOriginal'),
                                        icon: DIMENSION_ICONS[opt.id],
                                        // Only the sizes this file size allows stay open (all of them for Custom).
                                        disabled: Boolean(allowedDimensions) && !allowedDimensions.includes(opt.id),
                                        // Every option explains itself; a locked one says why it is locked.
                                        hint: allowedDimensions && !allowedDimensions.includes(opt.id)
                                            ? t('compressImage.dimensionLockedHint', { kb: toBnDigits(targetKb) })
                                            : t(`compressImage.maxDimensions.${opt.id}`),
                                    }))}
                                />
                                <div className="mt-1.5 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                    {presetLocksJpg ? t('compressImage.maxDimensionAuto') : t('compressImage.maxDimensionHint')}
                                </div>
                            </div>

                            <SettingToggle
                                label={t('compressImage.preserveExif')}
                                hint={t('compressImage.preserveExifHint')}
                                // EXIF only survives JPEG → JPEG; say so instead of
                                // silently ignoring the switch for a PNG or a WebP.
                                note={preserveExif && files.some((f) => !canPreserveExif(f, effectiveFormat))
                                    ? t('compressImage.preserveExifJpgOnly')
                                    : null}
                                checked={preserveExif}
                                disabled={processing}
                                onChange={(next) => { setPreserveExif(next); invalidateResults(); }}
                            />

                            <SettingToggle
                                label={t('compressImage.keepOriginalName')}
                                hint={t('compressImage.keepOriginalNameHint')}
                                checked={keepOriginalName}
                                disabled={processing}
                                onChange={(next) => { setKeepOriginalName(next); invalidateResults(); }}
                            />
                        </div>
                    </ToolCard>

                    {/* Same picker as every other image tool — AVIF included, written
                        in WebAssembly where the browser cannot (see compressImageEngine). */}
                    <OutputFormatPicker
                        value={effectiveFormat}
                        onChange={(next) => { setFormat(next); invalidateResults(); }}
                        disabled={processing}
                        // A preset size fixes the format to JPG; Custom frees it again.
                        enabledIds={presetLocksJpg ? ['jpg'] : null}
                        lockedHint={t('compressImage.formatLockedHint', { kb: toBnDigits(targetKb) })}
                        transparentOutput
                    />
                    {presetLocksJpg ? (
                        <p className="-mt-1 px-1 text-[11.5px] leading-relaxed text-[#A8A099] dark:text-zinc-500">
                            {t('compressImage.sizeJpgNote')}
                        </p>
                    ) : null}
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    // Fixed height suits one or two big pictures; a grid of three or more just hugs its rows.
                    tall={files.length <= 2}
                    title={t('compressImage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('compressImage.previewEmpty')}
                    idleHint={t('compressImage.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={overallProgress}
                    error={error}
                    canvasTone="plain"
                    processingTone="none"
                    showPasteHint
                    showIdle={!files.length && !error}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                >
                    {/* Kept mounted while processing: each card spins on its own, so
                        the wait shows the work and finished files can be opened. */}
                    {(files.length > 0 || results.length > 0) && !error ? (
                        <ImageGridPreview
                            // One picture fills the panel, two sit side by side, three or more make a grid.
                            adaptive
                            actions={processing || results.length ? [] : fileActions}
                            files={files}
                            results={results}
                            busyIndexes={processing ? pendingIndexes : undefined}
                            activeIndex={results.length ? previewIndex : undefined}
                            onItemClick={results.length ? openLightbox : undefined}
                            onAddMore={processing ? undefined : () => fileInputRef.current?.click()}
                            onAddDrop={processing ? undefined : handleDrop}
                            addLabel={t('compressImage.addMore')}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
        {cropDialog}
        <ImagePreviewModal
            opened={lightboxIndex !== null}
            onClose={() => setLightboxIndex(null)}
            images={compareItems}
            initialImage={compareItems.find((item) => item.id === lightboxIndex) ?? null}
            // `adjusted` is the compressed image re-rendered with the viewer's
            // brightness/contrast/sharpness edits burnt in; without edits it is
            // null and the untouched result goes out.
            onDownloadImage={(img, adjusted) => {
                const r = results[img?.id];
                if (!r) return;
                downloadBlob(adjusted || r.blob, r.name);
            }}
            initialViewMode="side-by-side"
            useImageTitle
            hideEdit
            hidePrint
            hideShare
            hideCustomDownload
            hideDelete
        />
        </>
    );
}
